import React, { useState, forwardRef } from "react";
import { IconButton, IconButtonProps } from "@/shared/ui/IconButton";
import { IconName } from "@/shared/ui/Icon";
import { Row } from "@/shared/ui/Blocks";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/utils/shadcn";

interface InlineConfirmButtonProps extends Omit<IconButtonProps, "onClick"> {
  icon: IconName;
  onClick: () => void;
  onCancel?: () => void;
  confirmLabel?: string;
  confirmDescription?: string;
  label?: string;
  loading?: boolean;
  showLabels?: boolean;
}

const InlineConfirmButton = forwardRef<
  HTMLButtonElement,
  InlineConfirmButtonProps
>((props, ref) => {
  const {
    onClick,
    onCancel,
    confirmLabel,
    confirmDescription,
    className,
    showLabels = false,
    size = "md",
    ...iconButtonProps
  } = props;

  const { t } = useTranslation();
  const [isConfirming, setIsConfirming] = useState(false);

  const handleInitialPress = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsConfirming(true);
  };

  const handleConfirm = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick();
    setIsConfirming(false);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsConfirming(false);
    if (onCancel) {
      onCancel();
    }
  };

  if (isConfirming) {
    return (
      <Row className={cn("gap-2 items-center")}>
        {confirmDescription && (
          <p className="text-sm text-gray-600">{confirmDescription}</p>
        )}
        <Row className="gap-2">
          <IconButton
            size={size}
            variant="destructive"
            onClick={handleConfirm}
            label={showLabels ? confirmLabel || t("common.confirm") : undefined}
            icon={"Check"}
          />
          <IconButton
            size={size}
            variant="outline"
            onClick={handleCancel}
            color="darkGrey"
            className="text-gray-600"
            label={showLabels ? t("common.cancel") : undefined}
            icon={"Close"}
          />
        </Row>
      </Row>
    );
  }

  return (
    <IconButton
      ref={ref}
      {...iconButtonProps}
      size={size}
      onClick={handleInitialPress}
      className={className}
    />
  );
});

InlineConfirmButton.displayName = "InlineConfirmButton";

export { InlineConfirmButton };
